import type { VersionChange } from '@/redux/internal-services/agentsApi';

/** Plain-language names for the top-level parts of an agent's configuration. Anything not listed falls back to its key
 *  with underscores turned into spaces, so a field added later still reads sensibly. */
const SECTION_LABELS: Record<string, string> = {
  agent_name: 'Agent name',
  description: 'Description',
  audio_prompt: 'Audio prompt',
  system_prompts: 'Prompt blocks',
  tools: 'Tools',
  tool_scope_overrides: 'Tool placement (modules & cards)',
  models: 'Models',
  model_parameters: 'Model settings',
  voice: 'Voice',
  status: 'Status',
  category: 'Category',
  tags: 'Tags',
  icon_url: 'Icon',
  trigger_type: 'Trigger',
  agent_type: 'Agent type',
  tenant_id: 'Tenant',
};

const words = (s: string) => s.replace(/_/g, ' ');

export const sectionLabel = (section: string): string => SECTION_LABELS[section] ?? words(section);

const short = (v: unknown, max = 60): string => {
  const text = typeof v === 'string' ? v : JSON.stringify(v);
  const flat = (text ?? '').replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
};

const q = (s: string) => `“${s}”`;
const isScalar = (v: unknown) => v === null || ['string', 'number', 'boolean'].includes(typeof v);

/** Display name of an item inside a list (a block, a tool, a screen): its label in the change path. */
const itemName = (c: VersionChange, at: number): string => c.path[at] ?? '';

/** One plain sentence saying what this change did. The exact before / after values are shown next to it. */
export function describeChange(c: VersionChange): string {
  const [section, ...rest] = c.path;
  const verb = c.type === 'added' ? 'Added' : c.type === 'removed' ? 'Removed' : 'Changed';
  const value = c.type === 'removed' ? c.old : c.new;

  // ── prompt blocks ─────────────────────────────────────────────────────────
  if (section === 'system_prompts') {
    if (rest.length === 0) return c.type === 'reordered' ? 'Reordered the prompt blocks' : `${verb} the prompt blocks`;
    const block = q(itemName(c, 1));
    if (rest.length === 1) {
      if (c.type === 'added') return `Added prompt block ${block}`;
      if (c.type === 'removed') return `Removed prompt block ${block}`;
    }
    const field = rest[1];
    if (field === 'prompt') return `Edited the text of prompt block ${block}`;
    if (field === 'title') return `Renamed prompt block ${q(short(c.old))} to ${q(short(c.new))}`;
    if (field === 'order') return `Moved prompt block ${block} (position ${short(c.old)} → ${short(c.new)})`;
    if (field === 'category') return `Changed the category of prompt block ${block} to ${q(short(c.new))}`;
    if (field) return `Changed ${words(field)} of prompt block ${block}`;
  }

  // ── tools ─────────────────────────────────────────────────────────────────
  if (section === 'tools') {
    if (rest.length === 0) return c.type === 'reordered' ? 'Reordered the tools' : `${verb} the tools`;
    const tool = q(itemName(c, 1));
    if (rest.length === 1) {
      if (c.type === 'added') return `Added tool ${tool}`;
      if (c.type === 'removed') return `Removed tool ${tool}`;
    }
    const field = rest[1];
    if (field === 'description') return `Edited the description of tool ${tool}`;
    if (field === 'name' || field === 'tool_name') return `Renamed tool ${q(short(c.old))} to ${q(short(c.new))}`;
    if (field === 'requires_confirmation') return `${c.new ? 'Turned on' : 'Turned off'} confirmation for tool ${tool}`;
    if (field === 'argument_schema') return `Changed the input schema of tool ${tool}`;
    if (field) return `Changed ${words(rest.slice(1).join(' › '))} of tool ${tool}`;
  }

  // ── where tools are placed (global list / per module or card) ─────────────
  if (section === 'tool_scope_overrides') {
    const [group, screen, name] = rest;
    if (group === 'global_tools') {
      if (c.type === 'reordered') return 'Reordered the globally available tools';
      if (name === undefined && screen) {
        return c.type === 'added'
          ? `Made tool ${q(screen)} available on every screen`
          : `Removed tool ${q(screen)} from the global list`;
      }
    }
    if (group === 'screens') {
      if (rest.length === 3) {
        return c.type === 'added'
          ? `Placed tool ${q(name)} on screen ${q(screen)}`
          : `Removed tool ${q(name)} from screen ${q(screen)}`;
      }
      if (rest.length === 2 && c.type === 'reordered') return `Reordered the tools on screen ${q(screen)}`;
      if (rest.length === 2) return `${verb} the placement list of screen ${q(screen)}`;
    }
    if (group === 'core_opt_out') return `Changed which core tools are taken off the global list`;
  }

  // ── anything else, including fields that do not exist yet ─────────────────
  const label = rest.length ? `${sectionLabel(section)} › ${rest.map(words).join(' › ')}` : sectionLabel(section);
  if (c.type === 'reordered') return `Reordered ${label.toLowerCase()}`;
  if (c.type === 'modified') {
    return isScalar(c.old) && isScalar(c.new) ? `Changed ${label}: ${q(short(c.old))} → ${q(short(c.new))}` : `Changed ${label}`;
  }
  if (c.type === 'added') return isScalar(value) ? `Set ${label} to ${q(short(value))}` : `Added ${label}`;
  return isScalar(c.old) && c.old !== '' ? `Cleared ${label} (was ${q(short(c.old))})` : `Removed ${label}`;
}

/** Changes that only restate another one (a block's position number moves whenever blocks are reordered, added or
 *  removed). They stay in the full comparison but are folded away from one-line summaries. */
export const isBookkeeping = (c: VersionChange): boolean => c.section === 'system_prompts' && c.path[2] === 'order';

/** A short summary of a version: the meaningful lines first, position renumbering folded into a single note. */
export function summarizeChanges(changes: VersionChange[], max = 6): { lines: string[]; more: number } {
  const meaningful = changes.filter((c) => !isBookkeeping(c)).map(describeChange);
  const renumbered = changes.some(isBookkeeping) && !changes.some((c) => c.section === 'system_prompts' && c.type === 'reordered');
  const all = renumbered ? [...meaningful, 'Renumbered the prompt blocks'] : meaningful;
  return { lines: all.slice(0, max), more: Math.max(0, all.length - max) };
}
